// Scrolling to a `#section` is the same problem on every page that has one: the
// browser's own jump is instant, lands under the fixed header, and never happens at
// all when the address was reached through the client router. Everything that links
// to a section goes through here instead.

// How long the target is allowed to keep moving before the scroll starts anyway.
const SETTLE_BUDGET_MS = 400;

/**
 * The link an editor wrote, split into the parts the click handler compares. Only
 * site addresses take part: an external link is left to the browser.
 *
 * A bare `#section` carries no path, which means the page being read.
 */
export function parseSectionLink(link: string): { path: string | null; hash: string } | null {
    if (!link) return null;
    if (link.startsWith("#")) return { path: null, hash: link.slice(1) };
    if (!link.startsWith("/")) return null;

    const address = new URL(link, "http://vahdatoptic.local");
    return { path: decodePath(address.pathname), hash: address.hash.slice(1) };
}

// A path written with its Persian segments encoded is the same path as one written
// with them spelled out, so both are read in the spelled out form. A trailing slash
// is not a different page either.
export function decodePath(pathname: string): string {
    let path = pathname;
    try {
        path = decodeURIComponent(pathname);
    } catch {
        // Left as it came: a half written escape is not worth failing the match over.
    }

    return path.replace(/\/+$/, "") || "/";
}

function findHashTarget(hash: string): HTMLElement | null {
    const id = hash.replace(/^#/, "");
    if (!id) return null;

    try {
        return document.getElementById(decodeURIComponent(id));
    } catch {
        return document.getElementById(id);
    }
}

// Where the page has to sit for the section to come to rest under the site header.
// `scrollMarginTop` on the element supplies the offset.
function measureTargetScrollY(target: HTMLElement) {
    const margin = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;

    return Math.max(0, target.getBoundingClientRect().top + window.scrollY - margin);
}

// A landing spot this far from where the scroll was aimed means the reader took over
// on the way down, and a page being read is never pulled out from under them.
const HANDOVER_TOLERANCE = 200;

// Under this the section is where it should be, and another move would only twitch.
const CORRECTION_THRESHOLD = 8;

/**
 * Scrolls to the section a hash names. The page is laid out when a shopper clicks a
 * link on it, so the first measurement is taken straight away — but images between
 * here and the section resolve their height while the page travels past them, which
 * leaves the section short of the header by however much they grew. Once the scroll
 * has come to rest the distance is re-read and closed, one time.
 */
export function scrollToHashTarget(hash: string) {
    const target = findHashTarget(hash);
    if (!target) return false;

    const intendedTop = measureTargetScrollY(target);
    window.scrollTo({ top: intendedTop, behavior: "smooth" });

    let corrected = false;
    const correct = () => {
        if (corrected) return;
        corrected = true;
        window.removeEventListener("scrollend", correct);
        window.clearTimeout(timeout);

        // The images the page travelled past finish resolving a moment after it stops
        // moving, so the distance is read once they have.
        window.setTimeout(() => {
            if (Math.abs(window.scrollY - intendedTop) > HANDOVER_TOLERANCE) return;

            const settledTop = measureTargetScrollY(target);
            if (Math.abs(settledTop - window.scrollY) <= CORRECTION_THRESHOLD) return;

            window.scrollTo({ top: settledTop, behavior: "smooth" });
        }, SETTLE_BUDGET_MS);
    };

    // Safari has no scrollend event, so the correction is on a timer there.
    window.addEventListener("scrollend", correct);
    const timeout = window.setTimeout(correct, 1200);

    return true;
}

/**
 * The same scroll, for a page that has just been opened. Components hydrate, images
 * above the target resolve their height, and every one of those moves the section
 * after the first measurement, so the target is re-read until it holds still.
 *
 * Returns the cleanup for the frame it has pending.
 */
export function scrollToHashTargetWhenSettled(hash: string) {
    const noop = () => { };
    if (!findHashTarget(hash)) return noop;

    const deadline = Date.now() + SETTLE_BUDGET_MS;
    let previous: number | null = null;
    let frame = 0;

    const tick = () => {
        // Re-read the element too: a section that renders after the first frame is
        // the reason the scroll is being retried at all.
        const target = findHashTarget(hash);
        if (!target) {
            if (Date.now() >= deadline) return;
            frame = window.requestAnimationFrame(tick);
            return;
        }

        const targetScrollY = measureTargetScrollY(target);
        const settled = previous !== null && Math.abs(targetScrollY - previous) < 1;

        if (settled || Date.now() >= deadline) {
            window.scrollTo({ top: targetScrollY, behavior: "smooth" });
            return;
        }

        previous = targetScrollY;
        frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frame);
}

/**
 * A page whose sections need more than a scroll margin to land on - the product list
 * has a sticky toolbar pinned above it - registers how to reach them while it is on
 * screen. Everything else falls back to the plain scroll, so the site-wide listener
 * never has to know which page it is on.
 */
export type HashScrollAdapter = {
    scroll: (hash: string) => boolean;
    scrollWhenSettled: (hash: string) => () => void;
};

let adapter: HashScrollAdapter | null = null;

export function registerHashScrollAdapter(next: HashScrollAdapter) {
    adapter = next;

    return () => {
        // A page that has already been replaced must not take its successor's adapter
        // down with it.
        if (adapter === next) adapter = null;
    };
}

/** Scrolls to a section, through whatever the page being read registered. */
export function scrollToSection(hash: string) {
    return adapter ? adapter.scroll(hash) : scrollToHashTarget(hash);
}

/** The same, for a page that has just been opened and is still settling. */
export function scrollToSectionWhenSettled(hash: string) {
    return adapter ? adapter.scrollWhenSettled(hash) : scrollToHashTargetWhenSettled(hash);
}

/** Whether the page has a section this hash names. */
export function hasHashTarget(hash: string) {
    return !!findHashTarget(hash);
}
